import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import app from "../firebase";

// Global BGMI / PUBG selector.
//
// This provider also OWNS the pop_battle_assets docs so:
//   - Both game docs (BGMI + PUBG) are fetched once on app mount and
//     shared across every route via context; tab switches don't hit
//     Firestore again.
//   - After the first fetch, the OTHER game's registration image and
//     rewards video are warmed into the browser HTTP cache with a
//     background fetch(). When the user toggles the picker, both
//     media assets are already cached and the UI paints instantly
//     instead of triggering a fresh multi-MB video download.
//   - Firebase Storage tokens are stable per-file, so once a doc is
//     cached the URLs work for the whole session (and the browser
//     cache keeps hitting until the admin re-uploads → new token →
//     new cache entry).

const GAMES = ["BGMI", "PUBG"];
// v2 bumped after switching the default from PUBG → BGMI, so users
// who had "PUBG" cached from before land on BGMI on their next
// visit instead of remembering the old default.
const STORAGE_KEY = "pa_pop_market_game_v2";
const DEFAULT_GAME = "BGMI";

const db = getFirestore(app);

const GameContext = createContext({
  game: DEFAULT_GAME,
  setGame: () => {},
  games: GAMES,
  popBattleDocs: {},
  popBattleLoading: true,
});

export function GameProvider({ children }) {
  const [game, setGameState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_GAME;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return GAMES.includes(stored) ? stored : DEFAULT_GAME;
  });
  const [popBattleDocs, setPopBattleDocs] = useState({});
  const [popBattleLoading, setPopBattleLoading] = useState(true);
  const warmedRef = useRef(new Set());

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, game);
    }
  }, [game]);

  // Fetch BOTH game docs once on mount and cache them. No more
  // per-page useEffect + getDoc — Directory and Calendar just read
  // the doc for the currently-selected game from this cache.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const entries = await Promise.all(
            GAMES.map(async (g) => {
              try {
                const snap = await getDoc(doc(db, "pop_battle_assets", g));
                return [g, snap.exists() ? snap.data() : null];
              } catch (err) {
                console.warn(`Failed to load pop_battle_assets/${g}:`, err);
                return [g, null];
              }
            }),
        );
        if (cancelled) return;
        setPopBattleDocs(Object.fromEntries(entries));
      } finally {
        if (!cancelled) setPopBattleLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Warm the browser HTTP cache with the OTHER game's assets so
  // switching the tab doesn't stall on a fresh multi-MB video
  // download. Runs once per (game, otherGame) pair per session.
  useEffect(() => {
    if (popBattleLoading || typeof window === "undefined") return;
    const otherGame = game === "BGMI" ? "PUBG" : "BGMI";
    const other = popBattleDocs[otherGame];
    if (!other) return;

    const toWarm = [
      other.registrationImageUrl,
      other.rewardsVideoUrl,
      other.rewardsImageUrl,
      other.rewardsVideos?.partnerPose?.url,
      other.rewardsVideos?.lobbyTheme?.url,
      other.rewardsVideos?.infoBackground?.url,
    ].filter(Boolean);

    for (const url of toWarm) {
      if (warmedRef.current.has(url)) continue;
      warmedRef.current.add(url);
      // no-cors so Firebase Storage's opaque response still gets
      // cached; we don't need to read the body, just prime the
      // browser cache so <video>/<img> reads from disk next time.
      fetch(url, { mode: "no-cors", credentials: "omit" }).catch(() => {
        warmedRef.current.delete(url); // let a later attempt retry
      });
    }
  }, [game, popBattleDocs, popBattleLoading]);

  const setGame = useCallback((g) => {
    if (GAMES.includes(g)) setGameState(g);
  }, []);

  return (
    <GameContext.Provider
      value={{
        game,
        setGame,
        games: GAMES,
        popBattleDocs,
        popBattleLoading,
      }}
    >
      {children}
    </GameContext.Provider>
  );
}

export function useGame() {
  return useContext(GameContext);
}
